import { useState, useEffect, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from './useAuth';
import {
  getRegistrationsByVolunteer,
  getUpcomingRegistrationsByVolunteer,
  getPastRegistrationsByVolunteer,
} from '@services/registrationService';
import { getEventById } from '@services/eventService';

/**
 * Custom hook to fetch and manage volunteer schedule data
 * @returns {Object} Schedule data and methods
 */
export const useSchedule = () => {
  const { currentUser } = useAuth();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Enrich registration with full event details
   * @param {Object} registration - Registration data
   * @returns {Promise<Object>} Enriched registration with event details
   */
  const enrichRegistration = async (registration) => {
    try {
      const event = await getEventById(registration.eventId);
      return { ...registration, event };
    } catch (err) {
      // Silently drop registrations whose events have been deleted or are inaccessible
      return { ...registration, event: null };
    }
  };

  /**
   * Fetch and categorize volunteer schedule
   */
  const fetchSchedule = useCallback(async () => {
    if (!currentUser?.uid) {
      console.log('⚠️ useSchedule: No current user, skipping fetch');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🔵 useSchedule: Fetching schedule for user:', currentUser.uid);

      // Fetch upcoming and past registrations in parallel
      const [upcomingRegs, pastRegs] = await Promise.all([
        getUpcomingRegistrationsByVolunteer(currentUser.uid),
        getPastRegistrationsByVolunteer(currentUser.uid),
      ]);

      console.log('📊 useSchedule: Fetched registrations:', {
        upcoming: upcomingRegs.length,
        past: pastRegs.length,
      });
      console.log('📋 Upcoming registrations:', upcomingRegs);
      console.log('📋 Past registrations:', pastRegs);

      // Enrich registrations with event details in parallel
      const [enrichedUpcoming, enrichedPast] = await Promise.all([
        Promise.all(upcomingRegs.map(enrichRegistration)),
        Promise.all(pastRegs.map(enrichRegistration)),
      ]);

      console.log('✨ Enriched registrations:', {
        upcoming: enrichedUpcoming.length,
        past: enrichedPast.length,
      });

      // Filter out any registrations where event fetch failed
      const validUpcoming = enrichedUpcoming.filter((reg) => reg.event !== null);
      const validPast = enrichedPast.filter((reg) => reg.event !== null);

      console.log('✅ Valid enriched registrations:', {
        upcoming: validUpcoming.length,
        past: validPast.length,
      });

      setUpcomingEvents(validUpcoming);
      setPastEvents(validPast);
    } catch (err) {
      console.error('❌ Error fetching schedule:', err);
      setError('Failed to load schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Refresh schedule data
   */
  const refresh = useCallback(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // Fetch schedule on mount and when user changes
  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  return {
    upcomingEvents,
    pastEvents,
    loading,
    error,
    refresh,
  };
};

export default useSchedule;
